import React, { useCallback, useEffect, useMemo, useState } from "react";
import "./customStyles.css";
import axios from "http/customAxios";
import EntityForm from "../EntityForm/EntityForm";
import { transformEntity } from "./utils";
import { entityToFormValuesMapper } from "../../Common/utils";
import {
  BOOKABLE_TYPE,
  CODE_TYPE,
  RATE_TYPE,
  RESTRICTION_CONFIGURATION_TYPE,
  RESTRICTION_TYPE,
  RESTRICTION_VALUE_TYPE,
  SHARING_TYPE,
} from "../../Common/constants";
import { withPropertyData } from "../../../../components/property-admin-data-provider";
import { useConfig } from "../../../../lib/config";
import { useChainData } from "../../../../components/chain-data-provider";
import { usePropertyInventoryData } from "../../../../components/inventory-provider";
import { FullWidthLoader } from "../../../../components/full-width-loader";
import styled from "styled-components";
import { generateBase58uuid } from "../utils";
import asBookingEngineEntity from "../Common/BookingEngineEntityHOC";
import CONSTANTS from "../constants";
import PromotionsBookingWindowForm from "./PromotionsBookingWindow/PromotionsBookingWindowForm";
import PromotionsBookingWindowList from "./PromotionsBookingWindow/PromotionsBookingWindowList";
import moment from "moment";
import { format } from "date-fns";
import { countryCodes } from "../../../../lib/intlUtils";
import { usePropertyConfiguration } from "../../../../components/property-configuration-provider";
import { useProperty } from "../../../../components/property-provider";
import { getEngineAppearance } from "../../../engineAppeareance/infrastructure/engineAppearance.api";
import { ReverseIconPack } from "../../../shared/widgets/DynamicIconList/IconPack.enum";
import { defineMessages, useIntl } from "react-intl";
import { Modal } from "react-bootstrap";
import PromotionEditByExcel from "../../../promotions/edit-by-excel";

export const ContainerLoader = styled.div`
  height: calc(71vh);
`;
const messages = defineMessages({
  iconPackMismatch: {
    id: "app.promotionsForm.iconPackMismatch",
    description: "iconPackMismatch",
    defaultMessage: "To continue, you must select an icon.",
  },
});

export function customEntityToFormValuesMapper({ chainInventories, isChain, onEditByExcel }) {
  return ({ entity, fields, locales, translationConfig }) => {
    const { inventories, propertyTickers, appliesToBookablesList } = chainInventories || {};
    const formValues = entityToFormValuesMapper({
      entity,
      fields,
      locales,
      translationConfig,
    });
    formValues.rate = 0;
    formValues.rateType = RATE_TYPE.PERCENTAGE;
    formValues.originalRateType = formValues.rateType;
    if (entity.discountPeriodicity) {
      formValues.rateType = RATE_TYPE.GIVE_NIGHT;
    } else if (entity.icon) {
      formValues.rateType = RATE_TYPE.HIGHLIGHT_MESSAGE;
    } else if (entity.rate && entity.rate.rateValue && entity.rate.rateValue.value) {
      formValues.rate =
        entity.rate.rateValue.valueType === RESTRICTION_VALUE_TYPE.MONEY
          ? entity.rate.rateValue.value.amount
          : entity.rate.rateValue.value;
      formValues.rateType = entity.rate.rateType;
      formValues.originalRateType = formValues.rateType;
    }
    if (formValues.rateType === RATE_TYPE.PERCENTAGE) {
      formValues.ratePercentage = formValues.rate;
    } else if (formValues.rateType === RATE_TYPE.CONSTANT) {
      formValues.rateConstant = formValues.rate;
    }

    formValues.restrictions = {
      [RESTRICTION_TYPE.OPEN_TRAVEL]: [
        {
          startDate: null,
          endDate: null,
          weekDays: [],
        },
      ],
    };

    if (
      entity.restrictions &&
      entity.restrictions[RESTRICTION_TYPE.OPEN_BOOKING] &&
      entity.restrictions[RESTRICTION_TYPE.OPEN_BOOKING].restrictionValue &&
      entity.restrictions[RESTRICTION_TYPE.OPEN_BOOKING].restrictionValue.value
    ) {
      entity.restrictions[RESTRICTION_TYPE.OPEN_BOOKING].restrictionValue.value.forEach(
        (restriction, key) => {
          formValues.bookingWindow = formValues.bookingWindow ? formValues.bookingWindow : [];
          formValues.bookingWindow[key] = {};
          formValues.bookingWindow[key].restrictions = {};
          formValues.bookingWindow[key].restrictions[RESTRICTION_TYPE.OPEN_BOOKING] = [];
          let formattedStartHour = null;
          if (restriction.startHour) {
            formattedStartHour = new Date();
            formattedStartHour.setHours(
              Number(`${restriction.startHour[0]}${restriction.startHour[1]}`)
            );
            formattedStartHour.setMinutes(
              Number(`${restriction.startHour[3]}${restriction.startHour[4]}`)
            );
          }
          let formattedEndHour = null;
          if (restriction.endHour) {
            formattedEndHour = new Date();
            formattedEndHour.setHours(Number(`${restriction.endHour[0]}${restriction.endHour[1]}`));
            formattedEndHour.setMinutes(
              Number(`${restriction.endHour[3]}${restriction.endHour[4]}`)
            );
          }
          formValues.bookingWindow[key].restrictions[RESTRICTION_TYPE.OPEN_BOOKING][0] = {
            startDate: moment(restriction.startDate).format("YYYY-MM-DD"),
            endDate: moment(restriction.endDate).format("YYYY-MM-DD"),
            weekDays: restriction.weekDays,
            startHour: formattedStartHour,
            endHour: formattedEndHour,
            timeZone: restriction.timeZone,
          };
        }
      );
    }

    if (entity.restrictions) {
      formValues[RESTRICTION_TYPE.CLOSE_TRAVEL] =
        entity.restrictions[RESTRICTION_TYPE.CLOSE_TRAVEL].configurationType ===
        RESTRICTION_CONFIGURATION_TYPE.OWN;
      formValues[RESTRICTION_TYPE.TODAY] = format(new Date(), "yyyy-MM-dd");
      formValues[RESTRICTION_TYPE.OPEN_TRAVEL_START_DATE] =
        entity.restrictions[RESTRICTION_TYPE.OPEN_TRAVEL].restrictionValue &&
        entity.restrictions[RESTRICTION_TYPE.OPEN_TRAVEL].restrictionValue.value &&
        entity.restrictions[RESTRICTION_TYPE.OPEN_TRAVEL].restrictionValue.value[0] &&
        entity.restrictions[RESTRICTION_TYPE.OPEN_TRAVEL].restrictionValue.value[0].startDate;
      formValues[RESTRICTION_TYPE.OPEN_TRAVEL_END_DATE] =
        entity.restrictions[RESTRICTION_TYPE.OPEN_TRAVEL].restrictionValue &&
        entity.restrictions[RESTRICTION_TYPE.OPEN_TRAVEL].restrictionValue.value &&
        entity.restrictions[RESTRICTION_TYPE.OPEN_TRAVEL].restrictionValue.value[0] &&
        entity.restrictions[RESTRICTION_TYPE.OPEN_TRAVEL].restrictionValue.value[0].endDate;

      formValues[RESTRICTION_TYPE.MIN_STAY] =
        entity.restrictions[RESTRICTION_TYPE.MIN_STAY].configurationType ===
        RESTRICTION_CONFIGURATION_TYPE.CONSTANT;
      if (entity.restrictions[RESTRICTION_TYPE.MIN_STAY].restrictionValue) {
        formValues[`${RESTRICTION_TYPE.MIN_STAY}_duration`] =
          entity.restrictions[RESTRICTION_TYPE.MIN_STAY].restrictionValue.value &&
          parseInt(entity.restrictions[RESTRICTION_TYPE.MIN_STAY].restrictionValue.value.duration);
      }

      formValues[RESTRICTION_TYPE.MAX_STAY] =
        entity.restrictions[RESTRICTION_TYPE.MAX_STAY].configurationType ===
        RESTRICTION_CONFIGURATION_TYPE.CONSTANT;
      if (entity.restrictions[RESTRICTION_TYPE.MAX_STAY].restrictionValue) {
        formValues[`${RESTRICTION_TYPE.MAX_STAY}_duration`] =
          entity.restrictions[RESTRICTION_TYPE.MAX_STAY].restrictionValue.value &&
          parseInt(entity.restrictions[RESTRICTION_TYPE.MAX_STAY].restrictionValue.value.duration);
      }

      formValues[RESTRICTION_TYPE.MIN_NOTICE] =
        entity.restrictions[RESTRICTION_TYPE.MIN_NOTICE].configurationType ===
        RESTRICTION_CONFIGURATION_TYPE.CONSTANT;
      if (entity.restrictions[RESTRICTION_TYPE.MIN_NOTICE].restrictionValue) {
        formValues[`${RESTRICTION_TYPE.MIN_NOTICE}_duration`] =
          entity.restrictions[RESTRICTION_TYPE.MIN_NOTICE].restrictionValue.value &&
          parseInt(
            entity.restrictions[RESTRICTION_TYPE.MIN_NOTICE].restrictionValue.value.duration
          );
      }

      formValues[RESTRICTION_TYPE.MAX_NOTICE] =
        entity.restrictions[RESTRICTION_TYPE.MAX_NOTICE].configurationType ===
        RESTRICTION_CONFIGURATION_TYPE.CONSTANT;
      if (entity.restrictions[RESTRICTION_TYPE.MAX_NOTICE].restrictionValue) {
        formValues[`${RESTRICTION_TYPE.MAX_NOTICE}_duration`] =
          entity.restrictions[RESTRICTION_TYPE.MAX_NOTICE].restrictionValue.value &&
          parseInt(
            entity.restrictions[RESTRICTION_TYPE.MAX_NOTICE].restrictionValue.value.duration
          );
      }

      formValues[RESTRICTION_TYPE.CODE] =
        entity.restrictions[RESTRICTION_TYPE.CODE] &&
        entity.restrictions[RESTRICTION_TYPE.CODE].restrictionValue &&
        entity.restrictions[RESTRICTION_TYPE.CODE].restrictionValue.value &&
        entity.restrictions[RESTRICTION_TYPE.CODE].restrictionValue.value.join(",");
      formValues.codeType = formValues[RESTRICTION_TYPE.CODE]
        ? CODE_TYPE.UNIVERSAL_CODE
        : CODE_TYPE.NO_CODE;

      formValues[RESTRICTION_TYPE.DEVICE_TYPE] =
        entity.restrictions[RESTRICTION_TYPE.DEVICE_TYPE] &&
        entity.restrictions[RESTRICTION_TYPE.DEVICE_TYPE].restrictionValue &&
        entity.restrictions[RESTRICTION_TYPE.DEVICE_TYPE].restrictionValue.value;

      const includedCountries =
        (entity.restrictions[RESTRICTION_TYPE.GEOLOCATION] &&
          entity.restrictions[RESTRICTION_TYPE.GEOLOCATION].restrictionValue &&
          entity.restrictions[RESTRICTION_TYPE.GEOLOCATION].restrictionValue.value) ||
        [];
      formValues[RESTRICTION_TYPE.GEOLOCATION] = includedCountries.length
        ? countryCodes.filter(it => !includedCountries.includes(it))
        : [];
    }

    if (entity.discountPeriodicity) {
      formValues.discountedUnits = entity.discountPeriodicity.discountedUnits;
      formValues.applyEveryNumberOfUnits = entity.discountPeriodicity.applyEveryNumberOfUnits;
      formValues.criteria = entity.discountPeriodicity.criteria;
      formValues.isRepeatable = entity.discountPeriodicity.isRepeatable;
    }

    const hasInventories =
      Array.isArray(inventories) &&
      Array.isArray(propertyTickers) &&
      inventories.length &&
      propertyTickers.length;
    const isOldChainPromo = isChain && entity.id && entity.sharingType === SHARING_TYPE.SIMPLE;
    delete formValues.appliesToInventoriesList;
    if (isChain && hasInventories) {
      formValues.appliesToInventoriesList = appliesToBookablesList;
      formValues.inventories = inventories || undefined;
      formValues.propertyTickers = propertyTickers || undefined;
      formValues.appliesToInventories = undefined;

      if (entity.importedExcelResults) {
        const result = [];
        for (const propertyTicker of propertyTickers) {
          result.push(
            entity.importedExcelResults
              .filter(it => it.propertyTicker.value === propertyTicker && it.isActive)
              .map(it => it.inventoryLine.value)
          );
        }
        formValues.appliesToInventoriesList = result;
      }
    } else if (entity.appliesToBookables && entity.appliesToBookables[BOOKABLE_TYPE.INVENTORY]) {
      formValues.appliesToInventories =
        entity.appliesToBookables[BOOKABLE_TYPE.INVENTORY].value || [];

      if (!formValues.appliesToInventories.length && isOldChainPromo) {
        delete formValues.appliesToInventories;
      }
    }

    formValues.onEditByExcel = onEditByExcel;
    return { ...formValues };
  };
}

function hasIconPackMismatch(icon, propertyIconPack) {
  if (!icon) {
    return false;
  }
  const rawPromotionIconPack = icon.substring(0, 2).toLowerCase();
  const promotionIconPackTicker = rawPromotionIconPack === "md" ? "md" : rawPromotionIconPack;
  const promotionIconPack = ReverseIconPack[promotionIconPackTicker];
  return promotionIconPack !== propertyIconPack;
}
function PromotionsForm({
  config,
  locales,
  entities,
  entityError,
  entityResult,
  entityName,
  loading,
  getListPath,
  relatedEntities,
  updateEntity,
  entity,
  onSubmitEntity,
  id,
  history,
  hotelTicker,
  sendAPIRequest,
  fieldsConfig,
  iconPack,
  setEntity,
}) {
  const { name, validation, renderers, translation, locale, tabs, showMenu } = config;
  const intl = useIntl();
  const configuration = useConfig();
  const propertyConfig = usePropertyConfiguration();
  const chainData = useChainData();
  const propertyInventoryData = usePropertyInventoryData();
  const [chainInventories, setChainInventories] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [entitiesId, setEntitiesId] = useState([]);
  const [propertyTickers, setPropertyTickers] = useState([]);
  const [error, setError] = useState();
  const [fields, setFields] = useState(fieldsConfig);
  const [modalOpen, setModalOpen] = useState(false);

  const groups = useMemo(() => {
    if (propertyConfig) {
      const numberOfPromotionsGroup = parseInt(propertyConfig.numberOfPromotionsGroup);
      let groups = {};
      const maxGroup = numberOfPromotionsGroup || 3;
      for (let i = 1; i <= maxGroup; i++) {
        groups = {
          ...groups,
          [`GROUP_${i < 10 ? "0" : ""}${i}`]: {
            label: i,
          },
        };
      }
      return groups;
    }
  }, [propertyConfig]);

  useEffect(() => {
    if (groups) {
      fieldsConfig.form.find(
        fieldForm => fieldForm.fieldName === "group" && (fieldForm.optionDisplay = groups)
      );
      setFields(fieldsConfig);
    }
  }, [config, fieldsConfig, groups, propertyConfig]);

  const sortedFields = useMemo(() => {
    return fields && fields.form && fields.form.sort((a, b) => (a.order > b.order ? 1 : -1));
  }, [fields]);

  const nestedFormWidgets = useMemo(
    () => ({
      bookingWindow: asBookingEngineEntity(PromotionsBookingWindowForm, {
        entityName: CONSTANTS.ENTITY.PROMOTIONSBOOKINGWINDOW.name,
        type: "FORM",
      }),
    }),
    []
  );

  const nestedListWidgets = useMemo(
    () => ({
      bookingWindow: asBookingEngineEntity(PromotionsBookingWindowList, {
        entityName: CONSTANTS.ENTITY.PROMOTIONSBOOKINGWINDOW.name,
        type: "LIST",
      }),
    }),
    []
  );

  const onEditByExcel = useCallback(() => {
    setModalOpen(true);
  }, []);

  const callMapper = useMemo(() => {
    return customEntityToFormValuesMapper({
      chainInventories,
      isChain: chainData && chainData.isChain,
      onEditByExcel,
    });
  }, [chainData, chainInventories, onEditByExcel]);

  useEffect(() => {
    const isNoChain = chainData && !chainData.isChain;
    const chainInventoriesIsReady = !!Object.keys(chainInventories).length;
    if ((isNoChain || chainInventoriesIsReady) && isLoading && propertyConfig) {
      setIsLoading(false);
    }
  }, [
    chainData,
    chainInventories,
    entity.id,
    entity.isActive,
    entity.sharingType,
    isLoading,
    propertyConfig,
  ]);

  const getPropertyInventory = useCallback((propertyInventoryData, child) => {
    const propertyInventory = propertyInventoryData.find(
      property => property.propertyTicker === child
    );
    return (propertyInventory && propertyInventory.inventoryTickers) || [];
  }, []);

  useEffect(() => {
    async function validatePromoChildren() {
      let appliesToBookablesList = [];
      let entitiesId = [];
      let inventories = [];
      let propertyTickers = [];
      if (entity.id) {
        try {
          const response = await axios.get(
            `${configuration.apiEndpoint}/api/aggregator/${entity.propertyTicker}/engine/administration/promo/${entity.ticker}`
          );
          const { data } = response;
          chainData.children.forEach(child => {
            const childData = data.find(d => d.propertyTicker === child);
            if (childData) {
              appliesToBookablesList = [
                ...appliesToBookablesList,
                (childData.appliesToBookables &&
                  childData.appliesToBookables[BOOKABLE_TYPE.INVENTORY].value) ||
                  [],
              ];
              entitiesId = [...entitiesId, childData.id];
              propertyTickers = [...propertyTickers, childData.propertyTicker];
              inventories = [...inventories, getPropertyInventory(propertyInventoryData, child)];
            } else {
              appliesToBookablesList = [...appliesToBookablesList, []];
              entitiesId = [...entitiesId, ""];
              propertyTickers = [...propertyTickers, child];
              inventories = [...inventories, getPropertyInventory(propertyInventoryData, child)];
            }
          });
        } catch (error) {
          console.log("error", error);
        }
      } else {
        propertyTickers = chainData.children;
        inventories = propertyTickers.reduce((inventories, child) => {
          inventories = [...inventories, getPropertyInventory(propertyInventoryData, child)];
          return inventories;
        }, []);
      }

      setChainInventories({
        inventories,
        propertyTickers,
        appliesToBookablesList,
      });
      setEntitiesId(entitiesId);
      setPropertyTickers(propertyTickers);
    }
    const isEntityIsReady = (entity.id && entity.isActive !== undefined) || !entity.id;
    const isChain = chainData && chainData.isChain;
    const propertyInventoryIsReady = !!Object.keys(propertyInventoryData).length;
    if (isChain && isEntityIsReady && propertyInventoryIsReady && isLoading) {
      validatePromoChildren();
    }
  }, [
    chainData,
    config,
    configuration,
    entity.propertyTicker,
    entity.ticker,
    entity.id,
    entity.isActive,
    propertyInventoryData,
    isLoading,
    chainInventories,
    getPropertyInventory,
    entity.sharingType,
  ]);

  async function handleSubmit({ formValues }) {
    console.log("submitted ");
    formValues.restrictions[RESTRICTION_TYPE.OPEN_BOOKING] = [];
    formValues.bookingWindow.forEach(window =>
      formValues.restrictions[RESTRICTION_TYPE.OPEN_BOOKING].push(
        window.restrictions[RESTRICTION_TYPE.OPEN_BOOKING][0]
      )
    );
    const actualEntity = transformEntity({
      entity: { ...formValues },
      entitiesLength: entities && entities.length,
    });
    const isOldPromo = formValues.sharingType === SHARING_TYPE.SIMPLE;
    if (chainData.isChain && propertyTickers && propertyTickers.length && !isOldPromo) {
      const parentPropertyTicker = entity.propertyTicker;
      let query = propertyTickers.reduce((result, child, index) => {
        const appliesToInventories = formValues.appliesToInventoriesList[index];
        if (appliesToInventories && appliesToInventories.length) {
          const entity = {
            ...formValues,
            appliesToInventories,
            ticker: formValues.ticker,
            id,
          };
          const childEntity = {
            id: entitiesId[index] ? entitiesId[index] : generateBase58uuid(),
            ...transformEntity({
              entity,
              entitiesLength: entities && entities.length,
            }),
            parentPropertyTicker,
          };
          result = {
            ...result,
            [child]: {
              ...childEntity,
            },
          };
        }
        return result;
      }, {});
      query = {
        [parentPropertyTicker]: {
          id: entity.id ? entity.id : generateBase58uuid(),
          ...actualEntity,
        },
        ...query,
      };
      const url = `${configuration.apiEndpoint}/api/aggregator/${
        entity.propertyTicker
      }/engine/administration/promo/${entity.id ? "update" : "create"}`;
      try {
        await sendAPIRequest({
          options: {
            url,
            method: "put",
            data: query,
          },
          reloadEntities: false,
          ignoreState: false,
        });
        return history.push(`/${hotelTicker}/promotions/success`);
      } catch (error) {
        console.log(error);
        setError(error);
      }
    } else {
      onSubmitEntity({
        id: entity.id,
        entity: actualEntity,
      });
    }
  }

  function beforeFormStateSetHook({ field, newState }) {
    if (field !== "discountedUnits" && field !== "rate") {
      return newState;
    }
    const discountedUnits = newState.formValues.discountedUnits;
    const rate = newState.formValues.rate && parseFloat(newState.formValues.rate);
    if (!(rate === 0 || (rate > 0 && discountedUnits > 0))) {
      newState = {
        ...newState,
        formValues: { ...newState.formValues, icon: null },
      };
    }
    return newState;
  }

  const customValidate = useCallback(
    formValues => {
      if (hasIconPackMismatch(formValues.icon, iconPack)) {
        return intl.formatMessage(messages.iconPackMismatch);
      }
    },
    [iconPack, intl]
  );

  const handleImportResults = useCallback(
    results => {
      setModalOpen(false);
      setEntity({ ...entity, importedExcelResults: results });
    },
    [entity, setEntity]
  );

  if (isLoading) {
    return (
      <ContainerLoader>
        <FullWidthLoader />
      </ContainerLoader>
    );
  }
  let disabledFields;
  if (
    chainData &&
    !chainData.isChain &&
    entity.sharingType === SHARING_TYPE.SHARED &&
    Array.isArray(sortedFields)
  ) {
    disabledFields = sortedFields.map(it => ({ ...it, disabled: true }));
  }

  return (
    <div className="wbkv9-promotions-form">
      <EntityForm
        loading={loading}
        name={name}
        entity={entity}
        entities={entities}
        validation={validation}
        renderers={renderers}
        fields={disabledFields || sortedFields}
        locales={locales}
        translation={translation}
        onSubmit={handleSubmit}
        entityError={entityError || error}
        entityResult={entityResult}
        entityName={entityName}
        getListPath={getListPath}
        entityToFormValuesMapper={callMapper}
        relatedEntities={relatedEntities}
        locale={locale}
        updateEntity={updateEntity}
        beforeFormStateSetHook={beforeFormStateSetHook}
        tabs={tabs}
        nestedFormWidgets={nestedFormWidgets}
        nestedListWidgets={nestedListWidgets}
        showMenu={showMenu}
        customValidate={customValidate}
      />
      <Modal show={modalOpen} onHide={() => setModalOpen(false)} size="xl">
        <Modal.Header closeButton></Modal.Header>
        <Modal.Body>
          <PromotionEditByExcel
            id={entity.id}
            propertyTicker={entity.propertyTicker}
            onHandleImportResult={handleImportResults}
          />
        </Modal.Body>
      </Modal>
    </div>
  );
}

PromotionsForm.propTypes = {};

PromotionsForm.defaultProps = {
  locales: ["es", "ca", "en", "fr", "it", "de"],
  getListPath: function getListPath({ hotelTicker, entityName }) {
    return `/${hotelTicker}/${entityName}/`;
  },
};

const PromotionFormWrapper = props => {
  const [mutatedFields, setMutatedFields] = useState({ ...props.config.fields });
  const [iconPack, setIconPack] = useState(undefined);
  const [entity, setEntity] = useState(undefined);
  const propertyInfo = useProperty();
  useEffect(() => {
    if (!propertyInfo.token) {
      return;
    }
    getEngineAppearance(propertyInfo.token).then(ea => {
      const propertyIconPack = ReverseIconPack[ea.iconpack.ticker];
      mutatedFields.form.find(it => it.fieldName === "icon").iconPack = propertyIconPack;
      setMutatedFields(mutatedFields);
      setIconPack(propertyIconPack);
    });
    setEntity(props.entity);
  }, [mutatedFields, propertyInfo.token, props.entity]);

  if (!iconPack) {
    return <div>Loading...</div>;
  }
  return (
    <PromotionsForm
      {...props}
      iconPack={iconPack}
      fieldsConfig={mutatedFields}
      entity={entity}
      setEntity={setEntity}
    />
  );
};

export default withPropertyData(PromotionFormWrapper);
